import dayjs from "dayjs";
import pako from "pako";

import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { swSettings } from "app/common/services/swSettings";
import { getUIVersionCookie } from "app/components/ProUIVersionIndication/ProUIVersionIndication";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import DurationService from "app/services/DurationService";
import { UserResource } from "app/user-data/UserResource";

import { i18nFilter } from "../filters/i18nFilter";
import type { TranslationReplacements } from "../filters/i18nFilter";

import { DefaultFetchService } from "./fetchService";

export const REPORT_LINK_KEY = "wwo.report.link";
interface IDownloadOptions {
    pdfType: string; //the name of the folder in PDF internals project
    domain?: string;
    link_text?: string;
    country?: string;
    duration?: string;
}

//const pdfUrl = `${swSettings.swsites.pdf}/pdf/`;
// old lambda
const getPdfUrl = () =>
    swSettings.swsites.pro.includes("sandbox") ? "https://pdf.sandbox.similarweb.com/" : "https://pdf.similarweb.com/";

export class PdfExportService {
    private static _html: string;

    /**
     * Creates html markup with given body and assigns it to _html
     * @param body
     */
    static setHTML(body, className: string): void {
        const pdf = PdfExportService;
        pdf._html = PdfExportService.getHTMLTop();

        //Append stylesheets
        pdf.getStylesheetsForPDF().forEach(function (styleSheet) {
            pdf._html += styleSheet;
        });
        //Add Korean fonts support.
        pdf._html += "<style>@import url(//fonts.googleapis.com/earlyaccess/jejugothic.css);</style>";
        //Close head tag.
        pdf._html += "</head>";
        //Append body with <className>
        pdf._html += `<body class=${className}>`;
        //Append body
        pdf._html += body;
        //Close body and html tags
        pdf._html += "</div></body></html>";
    }

    static getHTMLTop() {
        let html = "<!DOCTYPE html><html><head><meta charset='UTF-8'>";
        //Append base url for public assets
        html += "<base href='" + window.location.origin + "'>";

        return html;
    }

    /**
     * Sends _html and title to server in order to receive PDF file after conversion
     * @param title
     * @returns {angular.IPromise<IResourceArray<T>>|angular.IPromise<Array<T>>|angular.IPromise<T>}
     */
    static downloadPDF(title: string, options: IDownloadOptions): Promise<any> {
        const htmlBlob = new Blob([PdfExportService._html], { type: "text/html" });
        const pdfOptions = {
            HtmlString: htmlBlob,
            Title: encodeURIComponent(title),
            Type: options.pdfType,
        };
        const optionalProperties = ["domainKey", "domain", "domains", "link", "country", "duration"];
        optionalProperties.forEach((property) => {
            if (options.hasOwnProperty(property)) {
                if (property === "domains") {
                    pdfOptions[PdfExportService.capitalizeFirstLetter(property)] = JSON.stringify(options[property]);
                } else {
                    pdfOptions[PdfExportService.capitalizeFirstLetter(property)] = options[property];
                }
            }
        });

        return UserResource.htmlToPdf(pdfOptions);
    }

    static capitalizeFirstLetter(input) {
        return input.charAt(0).toUpperCase() + input.slice(1);
    }

    /**
     * Extracts all stylesheets from the document for PDF export
     * Uses CSSOM API to comprehensively capture all styles including dynamic ones
     * Falls back to link references for cross-origin stylesheets
     * @returns {string[]} Array of style HTML strings
     */
    static getStylesheetsForPDF(): string[] {
        const collectedStyles: string[] = [];

        // Extract from document.styleSheets (covers everything: <style>, <link>, and dynamic)
        Array.from(document.styleSheets).forEach((sheet, idx) => {
            try {
                if (sheet.cssRules && sheet.cssRules.length > 0) {
                    const cssText = Array.from(sheet.cssRules)
                        .map((rule) => rule.cssText)
                        .join("\n");
                    collectedStyles.push(`<style data-pdf-sheet="${idx}">${cssText}</style>`);
                }
            } catch (_e) {
                // Cross-origin stylesheet - cannot access cssRules
                // Fallback: include the link reference if it exists
                if (sheet.href && sheet.ownerNode) {
                    const linkElement = sheet.ownerNode as HTMLLinkElement;
                    if (linkElement.tagName === "LINK") {
                        collectedStyles.push(linkElement.outerHTML);
                    }
                }
            }
        });

        return collectedStyles;
    }

    /**
     * Extracts all stylesheets from the document for PNG export
     * Combines both <style> tags and CSSOM-injected styles
     * @returns {string} Combined HTML string of all styles
     */
    static getStylesheetsForPng(): string {
        const allPageStyles: string[] = [];

        // Extract from document.styleSheets (covers everything: <style>, <link>, and dynamic)
        Array.from(document.styleSheets).forEach((sheet, idx) => {
            try {
                if (sheet.cssRules && sheet.cssRules.length > 0) {
                    // if the code encounters a cross-origin stylesheet
                    // it THROWS: DOMException: Failed to read 'cssRules'
                    // The .map() operation stops here
                    // Error propagates up
                    const cssText = Array.from(sheet.cssRules)
                        .map((rule) => rule.cssText)
                        .join("\n");
                    allPageStyles.push(`<style data-cssom-index="${idx}">${cssText}</style>`);
                }
            } catch (_e) {
                // Catches the SecurityError
                // Cross-origin stylesheet - cannot access cssRules, skip it
            }
        });

        return allPageStyles.join("");
    }

    static downloadPaidPdfFedService(
        linkKey: string,
        titleKey: string,
        options?: {
            date?: string;
            domain?: string;
            coverHref?: string;
            overrideParams?: Record<string, any>;
            linkHref?: string;
            duration?: string;
        },
    ): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            const params = proNavigator.getParams();
            const defaultDate = DurationService.getDurationData(params.duration).forWidget;
            const { date = defaultDate, domain, duration, linkHref, overrideParams = {} } = options ?? {};
            const fetchService = DefaultFetchService.getInstance();
            const translate = i18nFilter();
            const username = swSettings.user.username;
            const swSites = swSettings.swsites;

            fetchService
                .post(
                    `${swSites.pro}/export-pdf/`,
                    {
                        username,
                        // NOTE: For local development this should be hardcoded to staging!
                        // page: `https://staging-pro.sandbox.similarweb.com/${proNavigator.getStateUrl(
                        page: `${swSites.pro}/${proNavigator.getStateUrl(proNavigator.current(), {
                            ...params,
                            pdf: "true",
                            ...overrideParams,
                        })}`,
                        link: linkHref ?? translate(linkKey),
                        title: translate(titleKey),
                        date,
                        domains: domain ?? params.key,
                        keyword: params.keyword,
                        country: params.country || WORLDWIDE_COUNTRY_ID,
                        duration: duration ?? params.duration,
                        uiVersion: getUIVersionCookie(),
                        href: options?.coverHref,
                    },
                    {
                        headers: {},
                        credentials: "include",
                    },
                )
                .then(
                    (res: { Data: string }) => {
                        PdfExportService.downloadFileLocal(res.Data);
                        resolve();
                    },
                    (err) => {
                        reject(err);
                    },
                );
        });
    }

    static downloadPdfFedService(
        linkKey: string,
        titleKey: string,
        options?: { date?: string; domain?: string; coverHref?: string; overrideParams?: Record<string, any> },
    ): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            const params = proNavigator.getParams();
            const defaultDate = DurationService.getDurationData(params.duration).forWidget;
            const { date = defaultDate, domain } = options ?? {};
            const { overrideParams = {} } = options ?? {};
            const fetchService = DefaultFetchService.getInstance();
            const translate = i18nFilter();
            const username = swSettings.user.username;

            fetchService
                .post(
                    getPdfUrl(),
                    {
                        username,
                        page: `${swSettings.swsites.pro}/${proNavigator.getStateUrl(proNavigator.current(), {
                            ...params,
                            pdf: "true",
                            ...overrideParams,
                        })}`,
                        link: translate(linkKey),
                        title: translate(titleKey),
                        date,
                        domains: domain ?? params.key,
                        keyword: params.keyword,
                        country: params.country,
                        duration: params.duration,
                        uiVersion: getUIVersionCookie(),
                        href: options?.coverHref,
                    },
                    {
                        headers: {},
                        credentials: "include",
                    },
                )
                .then(
                    (res: { Data: string }) => {
                        PdfExportService.downloadFileLocal(res.Data);
                        resolve();
                    },
                    (err) => {
                        reject(err);
                    },
                );
        });
    }

    static downloadHtmlPdfFedService(title: string, domains = ""): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            const translate = i18nFilter();
            const swSites = swSettings.swsites;
            const htmlBlob = new Blob([pako.deflate(PdfExportService._html)], {
                type: "application/zlib",
            });
            const formData = new FormData();
            formData.append("title", encodeURIComponent(title));
            formData.append("date", dayjs().format("MMMM YYYY"));
            formData.append("domains", domains);
            formData.append("link", translate("wwo.report.link"));
            formData.append("htmlBlob", htmlBlob);

            fetch(`${swSites.pro}/export-pdf/html`, {
                method: "POST",
                body: formData,
                credentials: "include",
            }).then(
                async (response) => {
                    const result = await response.json();
                    PdfExportService.downloadFileLocal(result.Data);
                    resolve();
                },
                (err) => {
                    reject(err);
                },
            );
        });
    }

    static downloadHtmlPngFedService(html, title, width, height): Promise<any> {
        const cssUploadLink = document.querySelector("link.css-to-upload");
        html = (cssUploadLink?.outerHTML ?? "") + html;
        return new Promise((resolve, reject) => {
            const htmlBlob = new Blob([html], { type: "text/html" });
            const formData = new FormData();
            formData.append("title", title);
            formData.append("width", width);
            formData.append("height", height);
            formData.append("htmlBlob", htmlBlob);

            fetch(`${getPdfUrl()}savePng`, {
                method: "POST",
                body: formData,
            }).then(
                async (response) => {
                    const result = await response.json();
                    PdfExportService.downloadFileLocal(result.Data);
                    resolve(response.status);
                },
                (err) => {
                    reject(err);
                },
            );
        });
    }

    static downloadFileLocal(data): void {
        window.location = data;
    }
}
